import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    AsyncSelectInput,
    autoinject,
    bind,
    DateRangePicker,
    DisplayEventProfile,
    Duration,
    Enums,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    Nullable,
    SelectInput,
    SubTopBar,
    TimeSpanPicker,
    Title,
    toArray,
    toKeyArray,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, Form, FormGroup } from 'reactstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { AlertActionsStore } from '../AlertActions/AlertActionsStore';
import { CreateOrEditDisplayAlertActionModal } from '../AlertActions/CreateOrEditDisplayAlertActionModal';
import { DisplayFiltersStore } from '../DisplayFilters/DisplayFiltersStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { DisplayEventProfileStore } from './DisplayEventProfileStore';
import { DisplaySelectorCard } from '../DisplayFilters/DisplaySelectorCard';
import { uniq } from 'lodash';
import { DisplayAlertActions } from './DisplayAlertsList';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { BsFillCaretUpFill } from '@react-icons/all-files/bs/BsFillCaretUpFill';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { BsFillCaretDownFill } from '@react-icons/all-files/bs/BsFillCaretDownFill';

export interface ICreateOrEditAlertParams {
    readonly displayAlertActions: DisplayAlertActions;
    guid: string;
}

export interface IEditProfileLocationState {
    minDuration?: number;
    maxDuration?: number;
    downtimeIncidentTypes?: Mri.DisplayApi.DowntimeIncidentTypes[];
    displayFilter?: Mri.DisplayFilters.DisplayFilter;
}

export interface ICreateOrEditAlertProps
    extends RouteComponentProps<ICreateOrEditAlertParams, {}, IEditProfileLocationState> {
    readonly appStore: AppStore;
    readonly displaysStore: DisplaysStore;
    readonly displayFiltersStore: DisplayFiltersStore;
    readonly displayEventProfileStore: DisplayEventProfileStore;
}

@autoinject
@observer
export class CreateOrEditDisplayAlert extends React.Component<ICreateOrEditAlertProps> {
    public constructor(props: ICreateOrEditAlertProps) {
        super(props);

        this.vm = new DisplayListViewModel({
            onChange: this.onDisplayFilterChange,
            disabledFilters: ['accounts'],
            columns: [{ option: 'Serial Number' }],
        });
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            if (this.viewState == DisplayAlertActions.Edit || this.viewState == DisplayAlertActions.Clone) {
                await this.displayEventProfileStore.loadAsync(this.props.match.params.guid);
            }

            await this.accountsStore.loadAsync();
            await this.alertActionsStore.loadAllAsync();

            this.fillForm();

            if (this.viewState == 'edit' && !this.vm.displayFilter.displays?.length) {
                this.simpleDisplaySelector = false;
            }
            this.advanced = this.hasAdvancedOptions;

            await this.vm.loadMoreAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        const location = `/alert-rules`;

        let title: string;

        switch (this.viewState) {
            default:
            case DisplayAlertActions.Edit:
                title = `Edit Alert`;
                break;
            case DisplayAlertActions.Clone:
                title = `Clone Alert`;
                break;
            case DisplayAlertActions.Create:
                title = `Create Alert`;
                break;
        }

        return (
            <div className="container-fluid pt-3">
                <Title>{title}</Title>
                <SubTopBar>
                    <LinkContainer exact to={location}>
                        <a className="btn btn-link">
                            <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Alerts
                        </a>
                    </LinkContainer>
                </SubTopBar>

                {this.displayAlertForm && (
                    <Form onSubmit={this.submitAsync}>
                        <div className="row justify-content-center">
                            <div className="col-12 col-md-6 mb-3 pe-md-2">
                                <div className="card">
                                    <div className="card-header bg-btn-primary">Event Profile</div>
                                    <div className="card-body px-1">
                                        {!!this.displayAlertForm.globalErrors.length && (
                                            <div className="alert-danger p-2">
                                                <ul>
                                                    {this.displayAlertForm.globalErrors.map(
                                                        (e: string, idx: number) => (
                                                            <li key={idx}>{e}</li>
                                                        )
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                        <div className="px-2">
                                            <p>
                                                <strong>
                                                    {DisplayEventProfile.readableForm(
                                                        this.displayAlertForm.values,
                                                        this.alertAction
                                                    )}
                                                </strong>{' '}
                                            </p>

                                            <FormGroup color={this.displayAlertForm.fields.name.state}>
                                                <label>
                                                    <strong>Name</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.displayAlertForm.fields.name}
                                                    placeholder="Name"
                                                    required
                                                />
                                            </FormGroup>

                                            <div className="form-group">
                                                <label>
                                                    <strong>Alert Actions</strong>

                                                    <button
                                                        className="ms-3 btn btn-sm btn-primary"
                                                        onClick={this.openCreateAlertActionModal}
                                                    >
                                                        Create Action
                                                    </button>

                                                    <small className="px-2">
                                                        <strong>Account:</strong> {this.account?.accountName}
                                                    </small>
                                                </label>

                                                <br />

                                                <SelectInput
                                                    field={this.displayAlertForm.fields.alertActionGuid}
                                                    options={this.alertActionOptions}
                                                    className="d-inline-block"
                                                    placeholder="Actions"
                                                    required
                                                />

                                                {this.displayAlertForm.fields.alertActionGuid.value && (
                                                    <a
                                                        className="btn btn-sm btn-link"
                                                        onClick={this.openEditAlertActionModal}
                                                    >
                                                        <BsPencilSquare />
                                                    </a>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label>
                                                    <strong>Displays</strong>

                                                    <a
                                                        className="btn btn-link py-0"
                                                        onClick={this.toggleDisplaySelector}
                                                    >
                                                        {!this.simpleDisplaySelector ? 'Simple' : 'Advanced'}
                                                    </a>
                                                </label>

                                                {!this.simpleDisplaySelector && (
                                                    <p>
                                                        <small>
                                                            *Note: All Displays shown under the Display Filter will be
                                                            added to this Manifest*
                                                        </small>
                                                    </p>
                                                )}

                                                {this.simpleDisplaySelector && (
                                                    <AsyncSelectInput
                                                        className="w-100 react-select-container d-inline-block"
                                                        placeholder="Serial Number"
                                                        loadOptions={this.loadOptionsAsync}
                                                        getOptionLabel={x => x!}
                                                        getOptionValue={x => x!}
                                                        defaultOptions
                                                        isMulti
                                                        field={
                                                            this.displayAlertForm.fields.displayFilter!.fields.displays
                                                        }
                                                    />
                                                )}
                                            </div>

                                            <FormGroup color={this.displayAlertForm.fields.alertPeriod.state}>
                                                <label className="w-100">
                                                    <strong>Period of time between Alert e-mails</strong>
                                                </label>

                                                <TimeSpanPicker field={this.displayAlertForm.fields.alertPeriod} />
                                            </FormGroup>

                                            <FormGroup color={this.displayAlertForm.fields.displayEventIncidents.state}>
                                                <label>
                                                    <strong>Display Events/Incidents</strong>
                                                </label>

                                                <SelectInput
                                                    field={this.displayAlertForm.fields.displayEventIncidents}
                                                    options={this.downtimeOptions}
                                                    placeholder="Display Event/Incident"
                                                    isMulti
                                                />
                                            </FormGroup>

                                            <FormGroup color={this.displayAlertForm.fields.comment.state}>
                                                <label>
                                                    <strong>Comments</strong>
                                                </label>

                                                <Input
                                                    type="text"
                                                    field={this.displayAlertForm.fields.comment}
                                                    placeholder="Comments"
                                                    required
                                                />
                                            </FormGroup>

                                            <Button
                                                color="link"
                                                size="sm"
                                                className="px-0"
                                                onClick={this.toggleAdvanced}
                                                disabled={this.hasAdvancedOptions}
                                            >
                                                {this.advanced ? 'Hide' : 'Show'} Advanced Options{' '}
                                                <em>
                                                    {this.advanced ? <BsFillCaretUpFill /> : <BsFillCaretDownFill />}
                                                </em>
                                            </Button>
                                        </div>

                                        {(this.hasAdvancedOptions || this.advanced) && (
                                            <div className="border px-2">
                                                <FormGroup
                                                    color={
                                                        this.displayAlertForm.fields.displayEventFilter.fields.startTime
                                                            .state
                                                    }
                                                >
                                                    <label className="w-100">
                                                        <strong>Starts between</strong>
                                                    </label>

                                                    <DateRangePicker
                                                        showTimeSelect
                                                        showTimeSelectOnly
                                                        startDate={
                                                            this.displayAlertForm.fields.displayEventFilter.fields
                                                                .startTime
                                                        }
                                                        endDate={
                                                            this.displayAlertForm.fields.displayEventFilter.fields
                                                                .endTime
                                                        }
                                                        timezone="UTC"
                                                    />
                                                    <small className="form-text text-muted">
                                                        Range for events start time of day in the local timezone of the
                                                        display
                                                    </small>
                                                </FormGroup>

                                                <FormGroup
                                                    color={
                                                        this.displayAlertForm.fields.displayEventFilter.fields
                                                            .minDuration.state
                                                    }
                                                >
                                                    <label className="w-100">
                                                        <strong>Minimum Duration</strong>
                                                    </label>

                                                    <TimeSpanPicker
                                                        field={
                                                            this.displayAlertForm.fields.displayEventFilter.fields
                                                                .minDuration
                                                        }
                                                    />
                                                </FormGroup>

                                                <FormGroup
                                                    color={
                                                        this.displayAlertForm.fields.displayEventFilter.fields
                                                            .maxDuration.state
                                                    }
                                                >
                                                    <label className="w-100">
                                                        <strong>Maximum Duration</strong>
                                                    </label>

                                                    <TimeSpanPicker
                                                        field={
                                                            this.displayAlertForm.fields.displayEventFilter.fields
                                                                .maxDuration
                                                        }
                                                    />
                                                </FormGroup>

                                                <FormGroup
                                                    color={this.displayAlertForm.fields.displayEventExpiration.state}
                                                >
                                                    <label className="w-100">
                                                        <strong>Event/Alert Expiration</strong>
                                                    </label>

                                                    <TimeSpanPicker
                                                        field={this.displayAlertForm.fields.displayEventExpiration}
                                                    />

                                                    <small className="form-text text-muted">
                                                        Time for an Event/Alert to expire if never acknowledged
                                                    </small>
                                                </FormGroup>
                                            </div>
                                        )}

                                        <p className="pt-4">
                                            <strong>
                                                {DisplayEventProfile.readableForm(
                                                    this.displayAlertForm.values,
                                                    this.alertAction
                                                )}
                                            </strong>
                                        </p>

                                        <FormGroup>
                                            <Button
                                                type="submit"
                                                color="primary"
                                                disabled={
                                                    this.simpleDisplaySelector &&
                                                    (!this.displayAlertForm.fields.displayFilter.value.displays ||
                                                        this.displayAlertForm.fields.displayFilter.value.displays
                                                            .length == 0)
                                                }
                                            >
                                                <Loader loading={this.loading} showSpinner message="Applying..." />
                                                {!this.loading && (
                                                    <span>
                                                        {this.viewState == DisplayAlertActions.Create &&
                                                            'Create New Rule'}
                                                        {this.viewState == DisplayAlertActions.Edit && 'Update'}
                                                        {this.viewState == DisplayAlertActions.Clone &&
                                                            'Clone New Rule'}
                                                    </span>
                                                )}
                                            </Button>
                                        </FormGroup>
                                    </div>
                                </div>
                            </div>

                            {!this.simpleDisplaySelector && (
                                <div className="col-12 col-md-6 ps-md-2">
                                    <DisplaySelectorCard history={this.props.history} vm={this.vm} />
                                </div>
                            )}
                        </div>
                    </Form>
                )}

                {this.displayAlertForm && (
                    <Modal
                        className="modal-md"
                        title="Confirm Action"
                        modalHandle={this.confirmModalHandle}
                        btnClass="w-50"
                        footerClass="d-flex justify-content-center"
                    >
                        <p className="text-center">
                            Are you sure you want to
                            {this.viewState == 'create' && ' Create '}
                            {this.viewState == 'edit' && ' Update '}
                            profile <strong>{this.displayAlertForm.fields.name.value}</strong>?
                        </p>

                        <div className="mb-3 text-center">
                            <small>
                                <strong>
                                    {DisplayEventProfile.readableForm(this.displayAlertForm.values, this.alertAction)}
                                </strong>{' '}
                            </small>
                        </div>
                        <Loader loading={this.loadingInfo} showSpinner message="Loading Effect..." />
                        {!this.loadingInfo && !!this.info && (
                            <div className="text-center">
                                Affects <strong>{this.info.numDisplays}</strong> displays and{' '}
                                <strong>{this.info.numEvents}</strong> events
                            </div>
                        )}
                        {!this.loadingInfo && !!this.infoError && (
                            <div className="text-center">
                                <strong>An error occurred. But you can still apply the profile.</strong>
                                <small>
                                    {typeof this.infoError == 'string'
                                        ? this.infoError
                                        : typeof this.infoError == 'object' && typeof this.infoError.message == 'string'
                                            ? this.infoError.message
                                            : 'An error has occured. Please try again.'}
                                </small>
                            </div>
                        )}
                    </Modal>
                )}
                {this.displayAlertForm && (
                    <CreateOrEditDisplayAlertActionModal
                        viewState={this.alertActionViewState}
                        modalHandle={this.createAlertActionModal}
                        alertActionGuid={this.displayAlertForm.fields.alertActionGuid.value}
                        onClosed={this.onAlertActionModalClosed}
                    />
                )}
            </div>
        );
    }

    @bind
    @action
    private onAlertActionModalClosed(alertAction: Mri.DisplayAlerts.AlertAction) {
        if (this.alertActionViewState == 'create') {
            this.displayAlertForm!.fields.alertActionGuid.value = alertAction.alertActionGuid;
        }
    }

    @bind
    @action
    private openCreateAlertActionModal(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.alertActionViewState = 'create';
        this.createAlertActionModal.open();
    }

    @bind
    @action
    private openEditAlertActionModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.alertActionViewState = 'edit';
        this.createAlertActionModal.open();
    }

    @bind
    @action
    private toggleDisplaySelector() {
        this.vm.clearAll();
        if (this.account) {
            this.vm.displayFilter.accounts = [this.account];
        }
        this.simpleDisplaySelector = !this.simpleDisplaySelector;
    }

    @bind
    @action
    private toggleAdvanced() {
        this.advanced = !this.advanced;
    }

    @bind
    @action
    private onDisplayFilterChange(displayFilter: Mri.DisplayFilters.DisplayFilter) {
        if (!this.displayAlertForm) throw new Error('Form does not exist');

        this.displayAlertForm.fields.displayFilter.setValue(displayFilter);
    }

    @computed
    private get hasAdvancedOptions() {
        if (!this.displayAlertForm) return false;

        const { displayEventFilter, displayEventExpiration } = this.displayAlertForm.values;
        const { startTime, endTime, minDuration, maxDuration } = displayEventFilter;

        return (
            displayEventExpiration != null ||
            startTime != null ||
            endTime != null ||
            minDuration != null ||
            maxDuration != null
        );
    }

    @computed
    private get alertActionOptions() {
        const alertActions = toArray(this.alertActionsStore.alertActions);

        const multiAccount = uniq(alertActions.map(aa => aa.account)).length > 1;

        return alertActions.map(a => {
            const account = a.account ? this.accountsStore.accounts.get(a.account) : null;
            const accountMsg = a.account && multiAccount ? `${account?.accountName ?? a.account}: ` : '';

            return {
                value: a.alertActionGuid,
                label: `${accountMsg}${a.name}`,
            };
        });
    }

    @computed
    private get downtimeOptions() {
        const events = toKeyArray(Enums.PortalDisplayEventType).map(key => {
            return {
                value: Enums.toString(Enums.PortalDisplayEventType, key),
                label: Enums.toString(Enums.PortalDisplayEventType, key),
            };
        });

        const incidents = toKeyArray(Enums.PortalDowntimeIncidentType).map(key => {
            return {
                value: Enums.toString(Enums.PortalDowntimeIncidentType, key),
                label: Enums.toString(Enums.PortalDowntimeIncidentType, key),
            };
        });

        return incidents.concat(events);
    }

    @action
    private fillForm() {
        if (this.eventProfile) {
            const eventProfileEventTypes = this.eventProfile.displayEventFilter.displayEventTypes;
            const eventProfileIncidentTypes = this.eventProfile.displayEventFilter.downtimeIncidentTypes;

            const displayEvents = eventProfileEventTypes
                ? Enums.toStringArray(Enums.PortalDisplayEventType, eventProfileEventTypes)
                : [];
            const incidents = eventProfileIncidentTypes
                ? Enums.toStringArray(Enums.PortalDowntimeIncidentType, eventProfileIncidentTypes)
                : [];

            this.eventProfile.displayEventIncidents = displayEvents.concat(incidents);

            this.displayAlertForm = new FormStore<DisplayEventProfile>(this.eventProfile);

            if (this.viewState == DisplayAlertActions.Clone) {
                this.displayAlertForm.fields.name.setValue(`Copy of ${this.eventProfile.name}`);
                this.displayAlertForm.fields.displayEventProfileGuid.setValue(uuidv4generate());
            }
        } else {
            const model: Nullable<DisplayEventProfile> = {
                name: '',
                comment: '',
                displayEventProfileGuid: uuidv4generate(),
                displayEventStatus: null,
                displayEventExpiration: new Duration(2 * 24 * 3600 * 1000),
                alertActionGuid: null,
                alertPeriod: new Duration(900 * 1000),
                account: '',
                displayFilter: {
                    accounts: null,
                    addressSearch: null,
                    cities: null,
                    contracts: null,
                    contractOptions: null,
                    contractServiceLevels: null,
                    countries: null,
                    displayGroups: null,
                    displays: null,
                    nocServers: null,
                    partDefinitions: null,
                    productionProjects: null,
                    serialNumberSearch: null,
                    currentSoftwareVersions: null,
                },
                displayEventFilter: {
                    displayEventStatus: [],
                    displayEventTypes: [],
                    downtimeIncidentTypes: [],
                    current: null,
                    downtimeActiveIncident: null,
                    minDuration: null,
                    maxDuration: null,
                    startTime: null,
                    endTime: null,
                    creationStartDate: null,
                    creationEndDate: null,
                    detailsSearch: '',
                },
                displayEventIncidents: [],
            } as any;

            if (this.props.location.state?.minDuration)
                model.displayEventFilter!.minDuration = new Duration(this.props.location.state.minDuration * 1000);
            if (this.props.location.state?.maxDuration)
                model.displayEventFilter!.maxDuration = new Duration(this.props.location.state.maxDuration * 1000);
            if (this.props.location.state?.downtimeIncidentTypes)
                model.displayEventFilter!.downtimeIncidentTypes = this.props.location.state.downtimeIncidentTypes;
            if (this.props.location.state?.displayFilter) {
                Object.assign(model.displayFilter!, this.props.location.state.displayFilter); //TODO TEST UPGRADE
                this.simpleDisplaySelector = false;
            }

            this.displayAlertForm = new FormStore<DisplayEventProfile>(model);
        }

        this.vm.displayFilter = this.displayAlertForm.fields.displayFilter.value;
    }

    @bind
    private async loadOptionsAsync(value: string) {
        const search: Mri.Web.Portal.Controllers.Displays.SearchModel = {
            displayFilter: {
                serialNumberSearch: value,
                accounts: this.account ? [this.account] : undefined,
            },
            limit: 10,
            offset: 0,
            orderBy: '',
            searchGuid: uuidv4generate(),
            status: false,
            where: "",
            telemetry: false,
            aggregates: false,
        };
        const response = await this.displaysStore.loadWhereSearchAsync(search);
        if (response) {
            return response.result;
        } else {
            return [];
        }
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLFormElement>) {
        if (!this.displayAlertForm) throw new Error('Form does not exist');

        event.preventDefault();
        this.displayAlertForm.onSubmit();

        if (this.displayAlertForm.validate()) {
            this.confirmModalHandle.open();

            this.loadingInfo = true;

            if (this.alertAction) {
                this.displayAlertForm.fields.account.setValue(this.alertAction.account);
            }

            const dei = this.displayAlertForm.fields.displayEventIncidents.value;
            if (dei) {
                const displayEvents = dei
                    .map(e => Enums.fromString(Enums.PortalDisplayEventType, e)!)
                    .filter(e => e != null);
                const downtimeIncidents = dei
                    .map(e => Enums.fromString(Enums.PortalDowntimeIncidentType, e)!)
                    .filter(e => e != null);

                this.displayAlertForm.fields.displayEventFilter.fields.displayEventTypes.setValue(displayEvents);
                this.displayAlertForm.fields.displayEventFilter.fields.downtimeIncidentTypes.setValue(
                    downtimeIncidents
                );
            }

            try {
                this.infoError = undefined;
                this.info = await this.displayEventProfileStore.infoAsync(this.displayAlertForm.values);
            } catch (e: any) {
                this.infoError = e;
            } finally {
                this.loadingInfo = false;
            }

            if (await this.confirmModalHandle.confirm()) {
                this.loading = true;

                try {
                    if (this.viewState == DisplayAlertActions.Edit) {
                        await this.displayEventProfileStore.updateAsync(
                            this.props.match.params.guid,
                            this.displayAlertForm.values
                        );
                    } else if (
                        this.viewState == DisplayAlertActions.Create ||
                        this.viewState == DisplayAlertActions.Clone
                    ) {
                        await this.displayEventProfileStore.createAsync(this.displayAlertForm.values);
                    }

                    const location = `/alert-rules`;
                    this.props.history.push(location);
                } catch (e: any) {
                    if (e.modelState) {
                        this.displayAlertForm.clearGlobalErrors();
                        this.displayAlertForm.addErrors(e.modelState);
                    } else {
                        console.error(e);
                    }
                } finally {
                    this.loading = false;
                }
            }
        } else {
            console.log('invalid');
        }
    }

    @computed
    private get alertAction() {
        if (this.displayAlertForm?.fields.alertActionGuid.value) {
            return this.alertActionsStore.alertActions.get(this.displayAlertForm?.fields.alertActionGuid.value);
        }
        return;
    }

    @computed
    private get eventProfile() {
        return this.displayEventProfileStore.displayEventProfiles.get(this.props.match.params.guid);
    }

    @computed
    private get viewState() {
        return this.props.match.params.displayAlertActions;
    }

    @computed
    private get account() {
        if (this.alertAction) {
            return this.accountsStore.accounts.get(this.alertAction.account);
        }
    }

    @observable
    private loading = false;
    @observable
    private loadingInfo = false;
    @observable
    private displayAlertForm?: FormStore<DisplayEventProfile>;
    @observable private simpleDisplaySelector = true;
    @observable private advanced = false;
    private vm: DisplayListViewModel;
    @observable
    private info: Mri.DisplayEventProfiles.DisplayEventProfileInfo | undefined;
    @observable
    private infoError: any | undefined;
    @observable
    private alertActionViewState: 'create' | 'edit' = 'create';
    private confirmModalHandle = new ModalHandle();
    private createAlertActionModal = new ModalHandle();
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private displayEventProfileStore!: DisplayEventProfileStore;
    @inject
    private alertActionsStore!: AlertActionsStore;
}
